import type { ComponentProps } from 'react';

import { cn } from '../cn';
import { General, Shapes } from '../StrokeIcons';
import type { Size } from './types';
import {
  sizeFont,
  sizeHeight,
  sizeIconClass,
  sizeInnerGap,
  sizePaddingL,
  sizePaddingR,
  sizeRounded,
} from './types';

export type CheckboxOption = {
  value: string;
  label: string;
};

export type CheckboxProps = Omit<ComponentProps<'div'>, 'size' | 'onChange'> & {
  size?: Size;
  bordered?: boolean;
  text?: boolean;
  error?: string;
  disabled?: boolean;
  name?: string;
  options: CheckboxOption[];
  value?: string[];
  onChange?: (value: string[]) => void;
};

export default function Checkbox({
  size = 'md',
  bordered = true,
  text,
  error,
  disabled,
  name,
  options,
  value = [],
  onChange,
  className,
  ...props
}: CheckboxProps) {
  function toggle(optionValue: string) {
    if (disabled) return;
    const next = value.includes(optionValue)
      ? value.filter((v) => v !== optionValue)
      : [...value, optionValue];
    onChange?.(next);
  }

  return (
    <div
      {...props}
      className={cn(
        'inline-flex flex-col overflow-hidden font-sans transition-colors divide-y',
        sizeRounded(size),
        sizeFont(size),
        'border',
        bordered && !text && !error && 'border-slate-200 dark:border-charade-700 divide-slate-200 dark:divide-charade-700',
        bordered && !text && error && 'border-fair-pink-300 dark:border-fair-pink-900 divide-fair-pink-300 dark:divide-fair-pink-900',
        (!bordered || text) && 'border-transparent',
        !text && !disabled && !error && 'bg-white dark:bg-charade-950',
        !text && !disabled && error && 'bg-fair-pink-100 dark:bg-fair-pink-950',
        !text && disabled && 'bg-stone-50 dark:bg-slate-950',
        text && 'bg-transparent',
        !disabled && !error && 'text-slate-600 dark:text-slate-100',
        disabled && 'text-stone-400 dark:text-slate-700',
        error && 'text-fair-pink-600 dark:text-fair-pink-400',
        className,
      )}>
      {options.map((opt) => {
        const checked = value.includes(opt.value);
        return (
          <label
            key={opt.value}
            className={cn(
              'inline-flex items-center cursor-pointer transition-colors',
              sizeHeight(size),
              sizeInnerGap(size),
              !text && !disabled && !checked && 'hover:bg-haiti-100 dark:hover:bg-haiti-900',
              !text && checked && 'bg-haiti-50 dark:bg-haiti-950',
              text && !disabled && 'hover:bg-slate-100 dark:hover:bg-slate-800',
              disabled && 'cursor-not-allowed',
            )}>
            <input
              type="checkbox"
              name={name}
              value={opt.value}
              checked={checked}
              onChange={() => toggle(opt.value)}
              disabled={disabled}
              className="hidden"
            />
            <span className={cn('shrink-0 flex items-center justify-center', sizePaddingL(size))}>
              <span className={cn(sizeIconClass(size), '*:size-full', checked && 'text-haiti-500')}>
                {checked ? <General.CheckSquare /> : <Shapes.Square />}
              </span>
            </span>
            <span className={cn('flex-1 min-w-0 truncate', sizePaddingR(size))}>{opt.label}</span>
          </label>
        );
      })}
    </div>
  );
}
